import type { DealDetailResource, UpdateDealBody } from '@majuscule/contracts';
import { useMutation } from '@tanstack/react-query';
import { Check, Pencil, X } from 'lucide-react';
import { useState, type JSX } from 'react';
import { crmSend, errorMessage } from '../../lib/crm';
import { MutationError, useCrmRefresh } from './shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Intitulé d'une affaire, renommable sur place tant qu'elle est en cours :
 * une affaire se saisit souvent avant d'en connaître le sujet exact.
 * Entrée enregistre, Échap annule.
 */
export function DealTitle({
  deal,
  editable,
}: {
  deal: DealDetailResource['deal'];
  editable: boolean;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const [draft, setDraft] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: (title: string) => {
      const body: UpdateDealBody = { title };
      return crmSend<DealDetailResource>('PATCH', `/deals/${deal.id}`, body);
    },
    onSuccess: () => {
      setDraft(null);
      refresh();
    },
  });

  if (draft === null) {
    return (
      <div className="flex items-center gap-1.5">
        <h1 className="m-0 text-title">{deal.title}</h1>
        {editable ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Renommer l’affaire"
            title="Renommer l’affaire"
            className="text-muted-foreground"
            onClick={() => {
              rename.reset();
              setDraft(deal.title);
            }}
          >
            <Pencil aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    );
  }

  const title = draft.trim();
  const cancel = (): void => {
    setDraft(null);
    rename.reset();
  };
  return (
    <form
      className="grid max-w-2xl gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (title === deal.title) {
          cancel();
        } else if (title !== '') {
          rename.mutate(title);
        }
      }}
    >
      <span className="flex items-center gap-1.5">
        <Input
          autoFocus
          aria-label="Intitulé de l’affaire"
          value={draft}
          maxLength={200}
          aria-invalid={title === '' ? true : undefined}
          className="h-10 text-[20px] font-semibold tracking-[-0.01em]"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              cancel();
            }
          }}
        />
        <Button
          type="submit"
          size="icon-sm"
          aria-label="Enregistrer l’intitulé"
          disabled={title === '' || rename.isPending}
        >
          <Check aria-hidden="true" />
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Annuler" onClick={cancel}>
          <X aria-hidden="true" />
        </Button>
      </span>
      {title === '' ? (
        <span className="text-caption font-normal text-danger-fg">Indiquer un intitulé.</span>
      ) : null}
      <MutationError message={rename.isError ? errorMessage(rename.error, 'Renommage refusé.') : null} />
    </form>
  );
}
